import { useCallback } from 'react';
import { EarconName, useEarcon } from '../../hooks/useEarcon';
import { findNextIndex } from '../../util/findIndex';
export const useListKeyDownHandler = ({
  length,
  doLooping,
  selectedIndex,
  setFocusedIndex,
  onSelectFocusedItem,
  ulRef
}) => {
  const earconError = useEarcon({
    source: EarconName.Error
  });
  return useCallback(e => {
    if (length <= 0) {
      return;
    }
    const maxIndex = length - 1;
    const getPrevFocusedIndex = prevFocusedIndex => prevFocusedIndex ?? selectedIndex;
    const getItemsPerPage = () => {
      const parentElement = ulRef?.current?.parentElement;
      const scrollHeight = parentElement?.scrollHeight ?? 0;
      const pageHeight = parentElement?.offsetHeight ?? 0;
      const itemHeight = scrollHeight / length;
      return Math.round(itemHeight !== 0 && pageHeight !== 0 ? pageHeight / itemHeight : 10);
    };
    const isScrollable = elem => {
      const hasScrollableContent = elem.scrollHeight > elem.clientHeight;
      const overflowYStyle = window.getComputedStyle(elem).overflowY;
      const isOverflowHidden = overflowYStyle.indexOf('hidden') !== -1;
      return hasScrollableContent && !isOverflowHidden;
    };
    const getScrollableParent = elem => {
      return !elem || elem === document.body ? document.body : isScrollable(elem) ? elem : getScrollableParent(elem.parentNode);
    };
    const items = (ulRef?.current ? [...ulRef.current.querySelectorAll('ul li, div[role="listbox"] li')] : []).filter(i => i.getAttribute('role') !== 'separator' && i.getAttribute('role') !== 'presentation');
    const firstHeaderItem = ulRef?.current?.querySelector('ul > li[role="presentation"]');
    const getNonSeparatorItem = index => items[index];
    const scrollIntoView = (index, alignToTop) => {
      if (index === 0 && firstHeaderItem && items[0].closest('ul[role="group"]')) {
        firstHeaderItem?.scrollIntoView?.(alignToTop);
      } else {
        getNonSeparatorItem(index)?.scrollIntoView?.(alignToTop);
      }
    };
    const isDomItemVisible = index => {
      const elem = getNonSeparatorItem(index);
      const parent = getScrollableParent(ulRef?.current);
      if (!elem || !parent) {
        return false;
      }
      const itemRect = elem.getBoundingClientRect();
      const parentRect = parent.getBoundingClientRect();
      return itemRect.top >= parentRect.top && itemRect.bottom <= parentRect.bottom;
    };
    const getItemText = index => getNonSeparatorItem(index)?.textContent?.trim();
    const isItemDisabled = index => getNonSeparatorItem(index)?.getAttribute('aria-disabled') === 'true';
    switch (e.key) {
      case 'ArrowDown':
        setFocusedIndex?.(prevFocusedIndex => {
          const prevIndex = getPrevFocusedIndex(prevFocusedIndex) ?? -1;
          const index = prevIndex < length - 1 ? prevIndex + 1 : doLooping ? 0 : length - 1;
          // if (!isDomItemVisible(index)) {
          scrollIntoView(index, false);
          // }
          return index;
        });
        break;
      case 'ArrowUp':
        setFocusedIndex?.(prevFocusedIndex => {
          const prevIndex = getPrevFocusedIndex(prevFocusedIndex) ?? length;
          const index = prevIndex > 0 ? prevIndex - 1 : doLooping ? length - 1 : 0;
          // if (!isDomItemVisible(index)) {
          scrollIntoView(index, true);
          // }
          return index;
        });
        break;
      case 'PageDown':
        {
          const itemsPerPage = getItemsPerPage();
          setFocusedIndex?.(prevFocusedIndex => {
            const prevIndex = getPrevFocusedIndex(prevFocusedIndex) ?? -1;
            const isLast = prevIndex === maxIndex;
            const index = Math.min(prevIndex + itemsPerPage, isLast && doLooping ? 0 : maxIndex);
            scrollIntoView(index, false);
            return index;
          });
          break;
        }
      case 'PageUp':
        {
          const itemsPerPage = getItemsPerPage();
          setFocusedIndex?.(prevFocusedIndex => {
            const prevIndex = getPrevFocusedIndex(prevFocusedIndex) ?? 0;
            const isFirst = prevIndex === 0;
            const index = Math.max(prevIndex - itemsPerPage, isFirst && doLooping ? maxIndex : 0);
            scrollIntoView(index, true);
            return index;
          });
        }
        break;
      case 'End':
        {
          const index = length - 1;
          scrollIntoView(index, false);
          setFocusedIndex?.(index);
        }
        break;
      case 'Home':
        {
          const index = 0;
          scrollIntoView(index, true);
          setFocusedIndex?.(index);
        }
        break;
      case ' ': /* falls-through */
      case 'Enter':
        onSelectFocusedItem?.();
        break;
      default:
        if (e.key.trim().length === 1) {
          setFocusedIndex?.(prevFocusedIndex => {
            const prevIndex = getPrevFocusedIndex(prevFocusedIndex) ?? -1;
            const key = e.key.toLowerCase();
            const index = findNextIndex({
              length,
              start: prevIndex + 1,
              isValidIndex: index => !isItemDisabled?.(index) && !!getItemText(index)?.toLowerCase().startsWith(key),
              wrap: doLooping ?? false
            });
            if (index !== undefined) {
              if (!isDomItemVisible(index)) scrollIntoView(index, false);
            } else earconError.play();
            return index ?? prevIndex;
          });
          e.stopPropagation();
        }
        return;
    }
    e.preventDefault();
  }, [length, selectedIndex, ulRef, setFocusedIndex, onSelectFocusedItem, doLooping, earconError]);
};
//# sourceMappingURL=useListKeyDownHandler.js.map